/**
 * Shape Switcher — ds-upjv
 * =========================
 * Permet de piloter la forme globale de l'interface (rayons sémantiques)
 * via une classe sur <html> : shape-soft / shape-rounded (sharp = aucune classe).
 *
 * Deux variantes d'affichage, détectées automatiquement :
 *   1. Boutons segmentés : <div class="shape-switcher"> avec des
 *      [data-shape="sharp|soft|rounded"] (boutons ou inputs radio + labels).
 *   2. Select natif : <select class="shape-switcher"> avec des <option value="...">.
 *
 * Forme appliquée : choix mémorisé de l'utilisateur (localStorage 'ds-shape'),
 * sinon data-default-shape sur <html> (ou initShapeSwitcher({ defaultShape })),
 * sinon "sharp". Seul un choix explicite est mémorisé ; synchronisé entre tous
 * les shape-switchers de la page.
 *
 * Usage :
 *   import { initShapeSwitcher } from './components/shape-switcher/shape-switcher.js';
 *   initShapeSwitcher();
 */

const STORAGE_KEY = "ds-shape";
const SHAPE_CLASSES = { soft: "shape-soft", rounded: "shape-rounded" };
const VALID = ["sharp", "soft", "rounded"];

// Forme par défaut fixée par initShapeSwitcher({ defaultShape }) (prioritaire sur l'attribut)
let configuredDefault = null;

function getStoredShape() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(s) ? s : null;
  } catch (e) {
    return null;
  }
}

function getDefaultShape() {
  if (VALID.includes(configuredDefault)) return configuredDefault;
  const attr = document.documentElement.dataset.defaultShape;
  return VALID.includes(attr) ? attr : "sharp";
}

// Priorité : choix mémorisé de l'utilisateur > forme par défaut déclarée > "sharp"
function getShape() {
  return getStoredShape() || getDefaultShape();
}

// N'écrit rien : toggle(classe, force) ne retire pas une classe déjà correcte (pas de flash)
function applyShape(id) {
  if (!VALID.includes(id)) id = "sharp";
  const html = document.documentElement;
  Object.entries(SHAPE_CLASSES).forEach(([shape, cls]) => html.classList.toggle(cls, shape === id));
}

function persistShape(id) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch (e) {}
}

const LABELS = { sharp: "Carré", soft: "Adouci", rounded: "Arrondi" };
const SYNC_EVENT = "ds-shape:change";

// Synchronise l'état visuel d'UN switcher selon la forme courante.
// Re-scanne le DOM à chaque appel (pas de cache) → fonctionne entre instances.
function syncSwitcher(sw, current) {
  if (sw.tagName === "SELECT") {
    sw.value = current;
  } else if (sw.classList.contains("select-custom")) {
    const valueEl = sw.querySelector(".select-custom__value");
    if (valueEl) {
      valueEl.textContent = LABELS[current] || "Carré";
      valueEl.classList.remove("select-custom__value--placeholder");
    }
    const hidden = sw.querySelector(".select-custom__input");
    if (hidden) hidden.value = current;
    sw.querySelectorAll(".select-custom__option").forEach((opt) => {
      const active = opt.dataset.shape === current;
      opt.classList.toggle("select-custom__option--selected", active);
      opt.setAttribute("aria-selected", active ? "true" : "false");
    });
  } else {
    sw.querySelectorAll("[data-shape]").forEach((el) => {
      const active = el.dataset.shape === current;
      if (el.tagName === "INPUT" && el.type === "radio") {
        el.checked = active;
      } else {
        el.classList.toggle("active", active);
        el.setAttribute("aria-pressed", active ? "true" : "false");
      }
    });
  }
}

// Synchronise TOUS les switchers de la page (toutes instances confondues)
function syncAllInDocument() {
  const current = getShape();
  document.querySelectorAll(".shape-switcher").forEach((sw) =>
    syncSwitcher(sw, current)
  );
}

export function initShapeSwitcher(containerOrOptions = document, options = {}) {
  // Accepte initShapeSwitcher(conteneur), initShapeSwitcher({ defaultShape }) ou les deux
  let container = containerOrOptions;
  if (containerOrOptions && !(containerOrOptions instanceof Node)) {
    options = containerOrOptions;
    container = document;
  }
  if (VALID.includes(options.defaultShape)) configuredDefault = options.defaultShape;

  // Écouteur global unique : re-synchronise tous les switchers à chaque changement.
  // Lié ici (et non au top-level du module) pour survivre au tree-shaking du bundle.
  if (!window.__dsShapeSyncBound) {
    window.__dsShapeSyncBound = true;
    window.addEventListener(SYNC_EVENT, syncAllInDocument);
  }

  const switchers = Array.from(container.querySelectorAll(".shape-switcher"));

  // Compatibilité : élément global par ID
  const globalEl = document.getElementById("shape-switcher");
  if (globalEl && !switchers.includes(globalEl)) switchers.push(globalEl);

  // Applique le mode stocké au chargement (même sans switcher sur la page)
  applyShape(getShape());

  if (!switchers.length) return;

  // Seule une action de l'utilisateur mémorise la forme
  function setShape(id) {
    if (!VALID.includes(id)) return;
    applyShape(id);
    persistShape(id);
    // Notifie tous les switchers de la page (footer + page courante)
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { shape: id } }));
  }

  // Listeners
  switchers.forEach((sw) => {
    if (sw.dataset.listenerAttached) return;
    sw.dataset.listenerAttached = "true";

    if (sw.tagName === "SELECT") {
      sw.addEventListener("change", () => setShape(sw.value));
    } else if (sw.classList.contains("select-custom")) {
      // Select custom DS : CustomEvent('change', { detail: { value } })
      sw.addEventListener("change", (e) => {
        const val = e.detail?.value;
        const v = Array.isArray(val) ? val[0]?.value || val[0] : val?.value || val;
        if (v) setShape(v);
      });
    } else if (sw.querySelector('input[type="radio"][data-shape]')) {
      // Variante radio : on écoute le change natif (clic label ou clavier)
      sw.addEventListener("change", (e) => {
        const target = e.target;
        if (target.matches('input[type="radio"][data-shape]')) {
          setShape(target.dataset.shape);
        }
      });
    } else {
      // Variante boutons simples (sans radio) : délégation click
      sw.addEventListener("click", (e) => {
        const target = e.target.closest("[data-shape]");
        if (!target || !sw.contains(target)) return;
        e.preventDefault();
        setShape(target.dataset.shape);
      });
    }
  });

  // Synchronise les switchers fraîchement initialisés avec la forme courante
  const current = getShape();
  switchers.forEach((sw) => syncSwitcher(sw, current));
}
